import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../i18n';
import { PlayerListSection, TeamAssignPanel, AppIcon } from '../shared';
import { TeamsState, TeamId } from '../../types/teams';

interface LobbyPlayer {
  id: string;
  name: string;
  score: number;
  avatar?: string;
  teamId?: 'A' | 'B';
  isBot?: boolean;
}

interface LobbyPlayersColumnProps {
  players: LobbyPlayer[];
  teams?: TeamsState;
  showTeamAssign: boolean;
  canAssignAny: boolean;
  myPlayerId?: string;
  onSetTeam: (playerId: string, teamId: TeamId | null) => void;
  /** Host-only: spawn an AI practice bot */
  onAddAiPlayer?: () => void;
  onRemoveAiPlayer?: (playerId: string) => void;
  addAiDisabled?: boolean;
  /** AI bots currently connecting (show skeleton rows) */
  pendingAiCount?: number;
}

function prefersHover(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;
}

export const LobbyPlayersColumn: React.FC<LobbyPlayersColumnProps> = ({
  players,
  teams,
  showTeamAssign,
  canAssignAny,
  myPlayerId,
  onSetTeam,
  onAddAiPlayer,
  onRemoveAiPlayer,
  addAiDisabled,
  pendingAiCount = 0,
}) => {
  const { t } = useTranslation();
  const [infoOpen, setInfoOpen] = useState(false);
  const infoPinnedRef = useRef(false);
  const infoWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!infoOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      if (infoWrapRef.current?.contains(event.target as Node)) return;
      infoPinnedRef.current = false;
      setInfoOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      infoPinnedRef.current = false;
      setInfoOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [infoOpen]);

  const openInfo = () => setInfoOpen(true);
  const closeInfoIfUnpinned = () => {
    if (!infoPinnedRef.current) setInfoOpen(false);
  };
  const toggleInfo = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    infoPinnedRef.current = !infoPinnedRef.current;
    setInfoOpen(infoPinnedRef.current);
  };

  const aiPromo = (className?: string) =>
    onAddAiPlayer ? (
      <div className={className ? `lobby-ai-promo ${className}` : 'lobby-ai-promo'}>
        <p>{t('lobby.ai.promo')}</p>
        <p>{t('lobby.ai.promoDetail')}</p>
      </div>
    ) : null;

  return (
    <div className="host-players-column">
      {showTeamAssign ? (
        <TeamAssignPanel
          players={players}
          teams={teams}
          canAssignAny={canAssignAny}
          myPlayerId={myPlayerId}
          onSetTeam={onSetTeam}
        />
      ) : (
        <PlayerListSection
          players={players}
          variant="compact"
          collapsible
          maxVisible={4}
          pendingCount={pendingAiCount}
          pendingLabel={t('lobby.ai.addingPlayer')}
          emptyMessage={aiPromo('lobby-ai-promo--empty') || undefined}
          footer={
            onAddAiPlayer && (players.length > 0 || pendingAiCount > 0)
              ? aiPromo()
              : undefined
          }
          renderPlayerActions={(player) => {
            if (!player.isBot || !onRemoveAiPlayer) {
              return null;
            }

            return (
              <button
                type="button"
                className="lobby-player-remove-btn"
                onClick={() => onRemoveAiPlayer(player.id)}
                aria-label={t('lobby.ai.removePlayerAria', { name: player.name })}
                title={t('lobby.ai.removePlayerHint', { name: player.name })}
              >
                x
              </button>
            );
          }}
          title={
            <span className="lobby-players-header">
              <span className="lobby-players-header__title">
                <AppIcon name="people" fontSize="small" />{' '}
                {t('common.players', { count: players.length + pendingAiCount })}
              </span>
              {onAddAiPlayer && (
                <span className="lobby-add-ai-controls">
                  <button
                    type="button"
                    className="lobby-add-ai-btn lobby-add-ai-btn--inline"
                    onClick={onAddAiPlayer}
                    disabled={addAiDisabled}
                  >
                    {pendingAiCount > 0 ? t('lobby.ai.addingPlayer') : t('lobby.ai.addPlayer')}
                  </button>
                  <div
                    className="host-playing-info lobby-ai-info"
                    ref={infoWrapRef}
                    onMouseEnter={() => {
                      if (prefersHover()) openInfo();
                    }}
                    onMouseLeave={() => {
                      if (prefersHover()) closeInfoIfUnpinned();
                    }}
                  >
                    <button
                      type="button"
                      className="host-playing-info__button"
                      aria-label={t('lobby.ai.infoAria')}
                      aria-expanded={infoOpen}
                      aria-controls="lobby-ai-info-tooltip"
                      onClick={toggleInfo}
                    >
                      <AppIcon name="info" fontSize="inherit" />
                    </button>
                    {infoOpen && (
                      <div
                        id="lobby-ai-info-tooltip"
                        className="host-playing-info__tooltip"
                        role="tooltip"
                      >
                        <p>{t('lobby.ai.addPlayerHint')}</p>
                      </div>
                    )}
                  </div>
                </span>
              )}
            </span>
          }
        />
      )}
    </div>
  );
};
